import React from "react";
import { Flex, Tag, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DdqProdOpsWorkflowStatus } from "~/graphql/supergraphEnums";
import { STATUS_LABELS } from "../labels";
import { getCurrentTask, getDueProximity } from "./columnHelpers";
import * as styles from "./styles_vanilla.css";
import type { DueProximity } from "./columnHelpers";
import type { DdqQueueRow, TypedAGGridCellRenderParams } from "../types";

const STATUS_TEXT_COLOR: Record<DdqProdOpsWorkflowStatus, string> = {
  [DdqProdOpsWorkflowStatus.Pending]: Colors.Neutral900,
  [DdqProdOpsWorkflowStatus.InProgress]: Colors.Neutral900,
  [DdqProdOpsWorkflowStatus.InReview]: Colors.Neutral900,
  [DdqProdOpsWorkflowStatus.OnHold]: Colors.Neutral900,
  [DdqProdOpsWorkflowStatus.Completed]: Colors.Neutral900,
  [DdqProdOpsWorkflowStatus.Cancelled]: Colors.Neutral500,
};

const DUE_PROXIMITY_TINT: Record<Exclude<DueProximity, null>, string> = {
  overdue: styles.statusTintDanger,
  dueSoon: styles.statusTintWarning,
};

export const StatusRenderer: React.FC<TypedAGGridCellRenderParams<DdqQueueRow>> = ({
  data,
}) => {
  if (!data?.status) return null;

  const proximity = getDueProximity(data.dueAt);
  const tint = proximity ? DUE_PROXIMITY_TINT[proximity] : undefined;

  return (
    <Flex
      className={
        tint ? `${styles.cellContentFlexCenter} ${tint}` : styles.cellContentFlexCenter
      }
      alignItems="center"
      data-testid="ddq-prod-ops-queue-status-cell"
    >
      <Text variant="regular-sm" color={STATUS_TEXT_COLOR[data.status]}>
        {STATUS_LABELS[data.status]}
      </Text>
    </Flex>
  );
};

export const OfferingRenderer: React.FC<TypedAGGridCellRenderParams<DdqQueueRow>> = ({
  data,
}) => {
  const offerings = data?.offerings ?? [];

  if (offerings.length === 0) return null;

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center" gap="xsmall">
      {offerings.map((offering) => (
        <Tag key={offering.id} variant="neutral" label={offering.name} />
      ))}
    </Flex>
  );
};

export const AssigneeRenderer: React.FC<TypedAGGridCellRenderParams<DdqQueueRow>> = ({
  data,
}) => {
  const assignee = data ? getCurrentTask(data)?.assignee : undefined;

  if (assignee) {
    return (
      <Flex className={styles.cellContentFlexCenter} alignItems="center">
        <Text variant="regular-sm" color={Colors.Neutral900}>
          {assignee.fullName}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex className={styles.cellContentFlexCenter} alignItems="center">
      <Text variant="regular-sm" color={Colors.Neutral500}>
        Unassigned
      </Text>
    </Flex>
  );
};
